const BRACKET_REFERENCE_PATTERN = /\[(@?[A-Z0-9_#]+)\]/gi;
const CONDITION_PATTERN = /\[(@?[A-Z0-9_#]+)\]\s*(<>|!=|=|==|<=|>=|<|>)\s*(".*?"|'.*?'|\[@?[A-Z0-9_#]+\]|TRUE|FALSE|[^\s]+)/i;
const SUFFIX_PATTERN = /^(.*?)(#[A-Z])$/i;

const CONTROL_TYPE_BY_SUFFIX = {
  '#C': 'checkbox',
  '#D': 'select',
};

function cloneIr(ir) {
  return JSON.parse(JSON.stringify(ir));
}

function parseTechnicalSuffix(variableName = '') {
  const match = variableName.match(SUFFIX_PATTERN);

  if (!match) {
    return {
      name: variableName,
      suffix: null,
      controlType: null,
    };
  }

  const suffix = match[2].toUpperCase();

  return {
    name: match[1],
    suffix,
    controlType: CONTROL_TYPE_BY_SUFFIX[suffix] || null,
  };
}

function normalizeVariableName(variableName = '') {
  return parseTechnicalSuffix(variableName).name.toUpperCase();
}

function isSystemVariable(variableName = '') {
  return /^@/i.test(variableName);
}

function isWindowEditableClassification(classification) {
  return classification === 'user_input' || classification === 'read_prefilled';
}

function isDisplayControl(field) {
  return field?.type === 'display' || field?.control === 'display' || field?.controlType === 'label' || field?.controlType === 'display';
}

function getNodeText(node) {
  return node.raw || node.expression || node.text || '';
}

function normalizeOperator(operator) {
  if (operator === '==') {
    return '=';
  }

  if (operator === '!=') {
    return '<>';
  }

  return operator;
}

function normalizeValue(rawValue) {
  const value = rawValue.trim();

  if (/^TRUE$/i.test(value)) {
    return true;
  }

  if (/^FALSE$/i.test(value)) {
    return false;
  }

  if (/^\[@?[A-Z0-9_#]+\]$/i.test(value)) {
    return {
      type: 'variable',
      name: normalizeVariableName(value.slice(1, -1)),
    };
  }

  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    const unquotedValue = value.slice(1, -1);

    if (/^TRUE$/i.test(unquotedValue)) {
      return true;
    }

    if (/^FALSE$/i.test(unquotedValue)) {
      return false;
    }

    return unquotedValue;
  }

  return value;
}

function parseCondition(type, expression, line, raw) {
  const match = expression.match(CONDITION_PATTERN);

  if (!match || isSystemVariable(match[1])) {
    return null;
  }

  const variableInfo = parseTechnicalSuffix(match[1]);

  return {
    type,
    variable: variableInfo.name,
    suffix: variableInfo.suffix,
    operator: normalizeOperator(match[2]),
    value: normalizeValue(match[3]),
    line,
    raw,
  };
}

function collectBracketReferences(text) {
  const references = [];
  let match;

  BRACKET_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = BRACKET_REFERENCE_PATTERN.exec(text)) !== null) {
    references.push(match[1]);
  }

  return references;
}

function getCommandTargets(node) {
  if (!['read', 'compute'].includes(node.type)) {
    return [];
  }

  if (node.type === 'compute') {
    const targetMatch = getNodeText(node).match(/^\s*COMPUTE\s+\[([A-Z0-9_#]+)\]/i) || (node.expression || '').match(/^\s*\[([A-Z0-9_#]+)\]/i);
    return targetMatch ? [targetMatch[1]] : [];
  }

  const references = collectBracketReferences(getNodeText(node)).filter((reference) => !isSystemVariable(reference));

  if (node.type === 'read') {
    return references;
  }

  if (references.length > 0) {
    return references;
  }

  const firstToken = (node.expression || '').trim().match(/^([A-Z0-9_#]+)/i);

  return firstToken ? [firstToken[1]] : [];
}

function createVariableState(variable) {
  return {
    name: normalizeVariableName(variable.name),
    datatype: variable.datatype,
    length: variable.length,
    required: variable.required,
    value: null,
    controlType: null,
    datasource: null,
    bindings: [],
    usages: [],
  };
}

function ensureState(states, variableName, defaults = {}) {
  const normalizedName = normalizeVariableName(variableName);

  if (!normalizedName || isSystemVariable(variableName)) {
    return null;
  }

  if (!states[normalizedName]) {
    states[normalizedName] = {
      name: normalizedName,
      datatype: null,
      length: null,
      required: false,
      value: null,
      controlType: null,
      datasource: null,
      bindings: [],
      usages: [],
      ...defaults,
    };
  }

  return states[normalizedName];
}

function mergeControlState(state, field) {
  const controlType = field.controlType || field.control || field.type || null;

  if (controlType) {
    state.controlType = controlType;
  }

  if (controlType === 'checkbox') {
    state.value = false;
  }

  if (controlType === 'select') {
    state.value = null;
    state.datasource = (field.options || []).map((option) => ({
      text: option.label,
      value: option.value,
    }));
  }
}

function mergeDisplayState(state) {
  state.controlType = 'display';
}

function addUsage(state, usage) {
  state.usages.push(usage);
}

function buildVariableStates(ast, ir) {
  const states = {};

  Object.values(ir.variables || {}).forEach((variable) => {
    states[normalizeVariableName(variable.name)] = createVariableState(variable);
  });

  Object.values(ir.catalogs || {}).forEach((catalog) => {
    const state = ensureState(states, catalog.variable);

    if (!state) {
      return;
    }

    state.controlType = 'select';
    state.value = null;
    state.datasource = (catalog.options || []).map((option) => ({
      text: option.label,
      value: option.value,
    }));
  });

  (ir.defaults || []).forEach((defaultNode) => {
    const state = ensureState(states, defaultNode.target);

    if (!state) {
      return;
    }

    state.defaultType = defaultNode.defaultType;
    state.defaultSource = 'default';

    if (defaultNode.defaultType === 'default_catalog') {
      state.controlType = 'select';
      state.value = null;
      state.datasource = (defaultNode.options || []).map((option) => ({
        text: option.label,
        value: option.value,
      }));
    }

    if (defaultNode.defaultType === 'default_checkbox') {
      state.controlType = 'checkbox';
      state.value = Boolean(defaultNode.value);
      state.defaultValue = Boolean(defaultNode.value);
    }

    if (defaultNode.defaultType === 'default_value') {
      state.defaultValue = defaultNode.value;
      state.value = defaultNode.value;
    }

    if (defaultNode.defaultType === 'default_assignment') {
      state.defaultValue = {
        type: 'variable',
        name: normalizeVariableName(defaultNode.value),
        suffix: defaultNode.valueSuffix || null,
      };
      state.isSelfAssignmentDefault = Boolean(defaultNode.isSelfAssignment);
    }

    addUsage(state, {
      role: 'default',
      source: 'default',
      defaultType: defaultNode.defaultType,
      value: defaultNode.value,
      valueType: defaultNode.valueType,
      line: defaultNode.line,
      raw: defaultNode.raw,
    });
  });

  (ir.forms || []).forEach((form) => {
    (form.fields || []).forEach((field) => {
      if (field.type === 'field_row') {
        (field.parts || []).forEach((part) => {
          if (isDisplayControl(part) && part.variable && !isSystemVariable(part.variable)) {
            const state = ensureState(states, part.variable);

            if (!state) {
              return;
            }

            mergeDisplayState(state);
            state.bindings.push({
              type: 'window',
              role: 'display',
              formId: form.id,
              label: part.label,
              rowLabel: field.label,
              line: part.line || field.line,
            });
            addUsage(state, {
              role: 'display',
              source: 'window',
              formId: form.id,
              label: part.label,
              rowLabel: field.label,
              line: part.line || field.line,
              raw: part.raw || field.raw,
            });
            return;
          }

          if (!part.variable || isSystemVariable(part.variable) || !isWindowEditableClassification(part.variableClass)) {
            return;
          }

          const state = ensureState(states, part.variable);

          if (!state) {
            return;
          }

          mergeControlState(state, part);
          state.bindings.push({
            type: 'window',
            formId: form.id,
            label: part.label,
            rowLabel: field.label,
            line: part.line || field.line,
          });
          addUsage(state, {
            role: 'capture',
            source: 'window',
            formId: form.id,
            label: part.label,
            rowLabel: field.label,
            line: part.line || field.line,
            raw: part.raw || field.raw,
          });
        });
        return;
      }

      if (field.type === 'checkbox-group') {
        (field.options || []).forEach((option) => {
          if (!option.variable || isSystemVariable(option.variable) || !isWindowEditableClassification(option.variableClass)) {
            return;
          }

          const state = ensureState(states, option.variable);

          if (!state) {
            return;
          }

          state.controlType = 'checkbox';
          state.value = false;
          state.bindings.push({
            type: 'window',
            formId: form.id,
            label: option.label,
            groupLabel: field.label,
            line: option.line || field.line,
          });
          addUsage(state, {
            role: 'capture',
            source: 'window',
            formId: form.id,
            label: option.label,
            groupLabel: field.label,
            line: option.line || field.line,
            raw: option.raw || field.raw,
          });
        });
        return;
      }

      if (isDisplayControl(field) && field.variable && !isSystemVariable(field.variable)) {
        const state = ensureState(states, field.variable);

        if (!state) {
          return;
        }

        mergeDisplayState(state);
        state.bindings.push({
          type: 'window',
          role: 'display',
          formId: form.id,
          label: field.label,
          line: field.line,
        });
        addUsage(state, {
          role: 'display',
          source: 'window',
          formId: form.id,
          line: field.line,
          raw: field.raw,
        });
        return;
      }

      if (
        !field.variable ||
        isSystemVariable(field.variable) ||
        field.type === 'button' ||
        field.type === 'text' ||
        field.informational ||
        field.control === 'display' ||
        !isWindowEditableClassification(field.variableClass)
      ) {
        return;
      }

      const state = ensureState(states, field.variable);

      if (!state) {
        return;
      }

      mergeControlState(state, field);
      state.bindings.push({
        type: 'window',
        formId: form.id,
        label: field.label,
        line: field.line,
      });
      addUsage(state, {
        role: 'capture',
        source: 'window',
        formId: form.id,
        line: field.line,
        raw: field.raw,
      });
    });
  });

  ast.nodes.forEach((node) => {
    if (node.type === 'if') {
      const condition = parseCondition('if', node.expression || '', node.line, node.raw);
      const state = condition ? ensureState(states, condition.variable) : null;

      if (state) {
        addUsage(state, {
          role: 'condition',
          source: 'if',
          operator: condition.operator,
          value: condition.value,
          line: node.line,
          raw: node.raw,
        });
      }
    }

    if (node.type === 'require') {
      const condition = parseCondition('require', node.expression || '', node.line, node.raw);
      const state = condition ? ensureState(states, condition.variable) : null;

      if (state) {
        addUsage(state, {
          role: 'validation',
          source: 'require',
          operator: condition.operator,
          value: condition.value,
          line: node.line,
          raw: node.raw,
        });
      }
    }

    getCommandTargets(node).forEach((reference) => {
      const state = ensureState(states, reference);

      if (!state) {
        return;
      }

      addUsage(state, {
        role: node.type === 'compute' ? 'mutation' : 'read',
        source: node.type,
        line: node.line,
        raw: node.raw,
      });
    });
  });

  return states;
}

function normalizeLogicNode(logicNode, astNode) {
  if (!astNode) {
    return logicNode;
  }

  if (astNode.type === 'if') {
    const condition = parseCondition('if', astNode.expression || '', astNode.line, astNode.raw);

    return condition
      ? {
          ...condition,
          action: logicNode.action || null,
        }
      : logicNode;
  }

  if (astNode.type === 'require') {
    const condition = parseCondition('require', astNode.expression || '', astNode.line, astNode.raw);

    return condition || logicNode;
  }

  if (['read', 'compute'].includes(astNode.type)) {
    const targets = getCommandTargets(astNode).map((target) => normalizeVariableName(target));

    return {
      ...logicNode,
      variable: targets[0] || null,
      variables: targets,
      modifiesState: astNode.type === 'compute' || astNode.type === 'read',
    };
  }

  return logicNode;
}

export function analyzeVariableState(ast, ir) {
  const nextIr = cloneIr(ir);
  const astNodeByLine = new Map(ast.nodes.map((node) => [node.line, node]));

  nextIr.variableStates = buildVariableStates(ast, nextIr);
  nextIr.logic = (nextIr.logic || []).map((logicNode) => normalizeLogicNode(logicNode, astNodeByLine.get(logicNode.line)));

  return nextIr;
}
